import { DDSTooltip, DDSTypography } from 'den-design-system'
import { useMediaQuery, useTheme } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Chart } from 'chart.js'
import { ChevronRight } from '@carbon/icons-react'
import CopyOnClick from '../CopyOnClick/CopyOnClick'
import Modal from '../DDS/Modal/Modal'
import { ProjectType } from '../../../enum/ProjectType.enum'
import React from 'react'
import { Chart as ReactChart } from 'react-chartjs-2'
import StringConstants from '../../../constants/StringConstants'
import Table from '../DDS/Table/Table'
import Tooltip from '../../New/Tooltip/Tooltip'
import { generateRandomId } from '../../../utils/CommonUtils'
import palette from '../../../global/pallete'
import pallete from '../../../global/newPallete'
import { replaceUrl } from '../../../utils/UrlUtils'
import routeConstants from '../../../constants/RouteConstants'
import styled from '@emotion/styled'
import { useStoreContext } from '../../../store/StoreContext'

interface OverviewDoughnutChartProps {
  labels: string[]
  data: number[]
  colors: string[]
  hideLegend?: boolean
  centerTextTitle?: string
  centerTextContent?: string
  cutout?: string
  showOthers?: boolean
  exceptionGroupId?: any
}
interface Legend {
  label: string
  color: string
  onClick?: () => any
  setModel?: () => any
}

const Square = styled.div<{ color: string }>`
  height: 12px;
  width: 16px;
  background-color: ${({ color }) => color};
  display: inline-block;
`
const Legend: React.FC<Legend> = ({ color, label, onClick, setModel }) => {
  const store = useStoreContext()

  return (
    <div className={`flex flex-row gap-x-[8px] justify-start items-center`}>
      {label !== '' && <Square color={color} />}
      <DDSTooltip
        id={generateRandomId()}
        label={label as string}
        className='override-tooltip-arrow border max-w-[70%] break-all'
        position='left'
        shape='roundedRectangle'
        theme='light'
        style={{ zIndex: 99999 }}
      >
        <div
          className={`flex w-full justify-center ${label === StringConstants.OTHERS ? 'cursor-pointer' : 'none'}`}
          onClick={setModel}
        >
          <DDSTypography.Paragraph
            color={
              label === StringConstants.OTHERS
                ? color
                : pallete.colors.textDark4
            }
            size='para'
            variant='medium'
            className={`${label === StringConstants.OTHERS && 'flex items-center'} text-wrap truncate  ${store.uiStore.getSidebarExpanded() ? 'md:w-[14vw]  2xl:w-[20vw]' : 'sm:w-[14vw] md:w-[16vw] lg:w-[20vw]  2xl:w-[]'} `}
          >
            {label}{' '}
            {label === StringConstants.OTHERS && (
              <ChevronRight
                color={color}
                className='h-[16px] w-[16px] flex cursor-pointer'
              />
            )}
          </DDSTypography.Paragraph>
        </div>
      </DDSTooltip>
    </div>
  )
}
const OverviewDoughnutChart: React.FC<OverviewDoughnutChartProps> = ({
  data,
  labels,
  colors,
  hideLegend,
  centerTextTitle,
  centerTextContent,
  cutout = '80%',
  showOthers,
  exceptionGroupId
}) => {
  const theme = useTheme()
  const store = useStoreContext()
  const greaterThanLarge = useMediaQuery(theme.breakpoints.up('xl'))
  const [searchParams] = useSearchParams()
  const projectType = store.scopeStore.getSelectedProject().type
  const isFullStackProject = projectType === ProjectType.FULL_STACK_ONLY
  const navigate = useNavigate()

  const routeException = (message: string) => {
    const tabFromParam = searchParams.get('tab') || '1'

    const route = replaceUrl(
      routeConstants.ERROR_MONITORING,
      store.scopeStore.getScope()['organization-id'] as string,
      store.scopeStore.getScope()['project-id'] as string,
      store.scopeStore.getScope()['environment-id'] as string
    ).replace(
      ':id',
      String(
        exceptionGroupId || store.exceptionsStoreV2.getCurrentExceptionId()
      )
    )
    const updatedParams = new URLSearchParams(searchParams)
    updatedParams.set('page', '1')
    updatedParams.set('tab', tabFromParam)
    updatedParams.set('groupTab', '2')
    updatedParams.set('search-string', message)
    updatedParams.set('search-type', 'message')
    updatedParams.set('searchTerm', message)
    navigate({
      pathname: route,
      search: `?${updatedParams.toString()}`
    })
  }

  //code for "Others" data table
  const tableColumns = [
    {
      columnHeaderStyle: { width: '240px' },
      columnDataStyle: { width: '240px' },
      heading: (
        <div className='flex justify-start text-start row items-start'>
          <DDSTypography.Paragraph
            variant='regular'
            size='para'
            color={palette.colors.textDark5}
            className='whitespace-nowrap'
          >
            {isFullStackProject ? 'Exception Message' : 'Error Message'}
          </DDSTypography.Paragraph>
        </div>
      ),
      dataLabel: 'exceptionMessage',
      render: (text: any) => (
        <div className='flex items-center'>
          <div className='flex items-center w-full'>
            <Tooltip
              id={`tooltip-name-${text}`}
              label={text}
              position='top'
              className={`override-tooltip-arrow overflow-hidden max-h-[20vh] max-w-[90%] break-all ${
                typeof text === 'string' && text.length > 25 ? '' : '!hidden'
              }`}
            >
              <div className='flex justify-between gap-[8px]'>
                <div>
                  <DDSTypography.Paragraph
                    size='para'
                    variant='medium'
                    color={pallete.colors.textDark4}
                    className={`tooltip-container sm:block flex items-center overflow-hidden ${
                      store.uiStore.getSidebarExpanded()
                        ? 'max-w-[240px]'
                        : 'max-w-[240px]'
                    }`}
                  >
                    <span className='truncate'>
                      {text || text === 0 ? text : '-'}
                    </span>
                  </DDSTypography.Paragraph>
                </div>
              </div>
            </Tooltip>
            <div className='flex items-end justify-end'>
              <CopyOnClick value={text} />
            </div>
          </div>
        </div>
      )
    },
    {
      columnHeaderStyle: { width: '100px' },
      columnDataStyle: { width: '100px' },
      heading: (
        <div className='flex justify-center text-center items-center'>
          <DDSTypography.Paragraph
            variant='regular'
            size='para'
            color={palette.colors.textDark5}
            className='whitespace-nowrap'
          >
            Count
          </DDSTypography.Paragraph>
        </div>
      ),
      dataLabel: 'count',
      render: (text: any) => (
        <div className='flex justify-center text-center row items-center '>
          <DDSTypography.Paragraph variant='medium' size='para'>
            {text ? text : '-'}
          </DDSTypography.Paragraph>
        </div>
      )
    },
    {
      columnHeaderStyle: { width: '3%' },
      columnDataStyle: { width: '3%' },
      heading: '',
      dataLabel: 'exceptionMessage',
      render: (message: any) => {
        return (
          <div className='cursor-pointer flex justify-end items-end'>
            <ChevronRight
              size={16}
              onClick={() => {
                routeException(message)
              }}
              color={pallete.colors.FillButton}
            />
          </div>
        )
      }
    }
  ]
  const [tableData, setTableData] = React.useState<any[]>(
    store.exceptionsStoreV2
      .getStatisticsData()
      .others?.map((error: any, index: any) => {
        return {
          key: index,
          id: error.id,
          exceptionMessage: error.message,
          count: error.count
        }
      })
  )
  const [modalOpen, setModalOpen] = React.useState<boolean>(false)
  function drawText(ctx: any, text: any, x: any, y: any, config: any) {
    config = config || {}
    text = typeof text === 'string' ? text : ''

    const fontColor = config.fontColor || '#000000'
    const fontSize = config.fontSize || 12
    const fontFamily = config.fontFamily || 'Arial'
    const fontWeight = config.fontWeight || 'normal'
    const lineHeight = config.lineHeight || fontSize + 2
    const textAlign = config.textAlign
    const textBaseline = config.textBaseline
    const texts = text.split('\n') || ''
    let widerLineWidth = 0
    ctx.save()
    ctx.fillStyle = fontColor
    ctx.font = [fontWeight, fontSize + 'px', fontFamily].join(' ')
    if (textAlign) ctx.textAlign = textAlign
    if (textBaseline) ctx.textBaseline = textBaseline
    texts.forEach(function (value: any, i: any) {
      const lineWidth = ctx.measureText(value).width
      widerLineWidth = lineWidth > widerLineWidth ? lineWidth : widerLineWidth
      const fixedY = y + lineHeight * i
      ctx.fillText(value, x, fixedY)
    })
    ctx.restore()
    return {
      width: widerLineWidth,
      height: lineHeight * (texts.length - 1)
    }
  }
  const centerText = {
    id: 'centerText',
    afterDraw: function (chart: any, _: any, options: any) {
      if (options.text1 && options.text2) {
        const ctx = chart.ctx
        const canvas = chart.canvas
        const posX = canvas.width / (greaterThanLarge ? 2 : 4)
        const posY = canvas.height / (greaterThanLarge ? 2 : 4)
        drawText(ctx, options.text1, posX, posY - 15, {
          fontColor: palette.colors.textDark,
          fontSize: 14,
          textAlign: 'center'
        })
        drawText(ctx, options.text2, posX, posY + 11, {
          fontWeight: palette.fontWeight.bold,
          fontColor: palette.colors.primary,
          fontSize: 24,
          textAlign: 'center',
          textBaseline: 'hanging'
        })
      }
    }
  }
  Chart.register(centerText)
  const chartData = {
    labels: labels,
    datasets: [
      {
        data: data,
        backgroundColor: colors,
        borderWidth: 0,
        rotation: 90
      }
    ]
  }

  const options = {
    cutout,
    plugins: {
      legend: {
        display: false
      },
      centerText: {
        text1: centerTextTitle,
        text2: centerTextContent,
        fontStyle: 'Inter',
        sidePadding: 20,
        minFontSize: 25,
        lineHeight: 25
      },
      tooltip: {
        enabled: false
      }
    }
  }

  return (
    <div className='flex flex-row gap-x-[24px] w-fit items-center'>
      <div>
        <ReactChart
          type={'doughnut'}
          data={chartData}
          options={options}
          key={window.innerWidth}
          style={{ height: '200px', width: '200px' }}
        />
      </div>

      {!hideLegend && (
        <div className='flex flex-col gap-y-[8px] max-w-fit ml-[12px]'>
          {labels.map((label, index) => (
            <div key={index} className='break-words'>
              <Legend
                label={label}
                color={colors[index]}
                onClick={
                  label.startsWith(StringConstants.OTHERS)
                    ? () => setModalOpen(true)
                    : undefined
                }
                setModel={
                  label.startsWith(StringConstants.OTHERS)
                    ? () => setModalOpen(true)
                    : undefined
                }
              />
            </div>
          ))}
        </div>
      )}
      <Modal
        open={modalOpen}
        setOpen={setModalOpen}
        showHeader
        hideCloseIcon={false}
        title='Others'
        onClosePress={() => {
          setModalOpen(false)
        }}
      >
        <div className='min-w-[500px] h-[388px] flex flex-col items-start p-[24px] overflow-y-auto'>
          <Table
            totalCount={tableData?.length}
            columns={tableColumns}
            data={tableData}
            showPagination={false}
          />
        </div>
      </Modal>
    </div>
  )
}

export default OverviewDoughnutChart
